"use client"

import { useMemo, useEffect, useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, Users, Mail, Building, Eye, Edit } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { createRoute } from "@tanstack/react-router"
import MainAppRoute from "@/rootRoutes/MainApp"
import { useGetBuckets, useGetBucketsBucketIdContacts, usePutBucketsBucketId } from "openapi/api/endpoints"
//import { BucketStatus } from "openapi/api/model"
import { useQueryClient } from "@tanstack/react-query"
import { PersonProfileSheet } from "@/components/community/person-profile-sheet"


// ---------- Route ----------
const BucketDetailRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/buckets/$bucketId",
  component: BucketDetailsPage,
})
export default BucketDetailRoute

// ---------- RHF form shape ----------
type BucketFormValues = {
  name: string
  description: string | null
  interval_days: number
  status: string //BucketStatus | undefined
}

function BucketDetailsPage() {
  const { bucketId } = BucketDetailRoute.useParams()
  const navigate = BucketDetailRoute.useNavigate()
  const queryClient=useQueryClient()
  const { data: apiBuckets, isLoading } = useGetBuckets()
  const {mutateAsync:updateBucket}=usePutBucketsBucketId()
  const currentBucket = useMemo(
    () => apiBuckets?.find((b) => b.id === bucketId),
    [apiBuckets, bucketId]
  )
  const {data:people}=useGetBucketsBucketIdContacts(bucketId)


  // Edit mode (UI-only toggle is fine to keep in state)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)

  // RHF setup — we’ll reset whenever currentBucket changes
  const { control, handleSubmit, reset, watch, setValue } = useForm<BucketFormValues>({
    defaultValues: {
      name: currentBucket?.name ?? "",
      description: currentBucket?.description ?? "",
      interval_days: currentBucket?.interval_days ?? 7,
      status: currentBucket?.status,
    },
  })

  useEffect(() => {
    if (currentBucket) {
      reset({
        name: currentBucket.name ?? "",
        description: currentBucket.description ?? "",
        interval_days: currentBucket.interval_days ?? 7,
        status: currentBucket.status,
      })
    }
  }, [currentBucket, reset])

  // Derived display values (live while editing)
  const watchName = watch("name")
  const watchStatus = watch("status")



  const handlePersonClick = (personId: string) => {
    setSelectedPersonId(personId)
  }

  const onSubmit = handleSubmit(async (values) => {
    console.log({values})
    await updateBucket({
      bucketId,
      data:{
        description:values.description??'',
        name:values.name,
        interval_days:values.interval_days

      }
    })
    queryClient.invalidateQueries()
    setIsEditing(false)
  })

  const onCancel = () => {
    reset({
      name: currentBucket?.name ?? "",
      description: currentBucket?.description ?? "",
      interval_days: currentBucket?.interval_days ?? 7,
      status: currentBucket?.status,
    })
    setIsEditing(false)
  }

  const toggleBucketStatus = () => {
    const next =
      (watchStatus ?? "active") === "active" ? ("paused") : ("active")
    setValue("status", next, { shouldDirty: true })
  }

  if (isLoading) return <div className="p-6">Loading…</div>
  if (!currentBucket) return <div className="p-6">Bucket not found.</div>

  const displayName = isEditing ? watchName : currentBucket.name
  const displayStatus = isEditing ? watchStatus : currentBucket.status

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/community" })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{currentBucket.name}</h1>
            <p className="text-muted-foreground">{currentBucket.description ?? ""}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setIsEditing((v) => !v)}>
          <Edit className="h-4 w-4 mr-2" />
          {isEditing ? "Close" : "Edit Bucket"}
        </Button>
      </div>

      {/* Bucket Editing Form */}
      {isEditing && (
        <form onSubmit={onSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Edit Bucket</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Name</Label>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => <Input {...field} />}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => <Textarea {...field} value={field.value ?? ""} />}
                />
              </div>

              <div>
                <Label>Touch Point Interval</Label>
                <Controller
                  control={control}
                  name="interval_days"
                  render={({ field }) => (
                    <Select
                      value={String(field.value ?? 7)}
                      onValueChange={(v) => field.onChange(parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">Every 3 days</SelectItem>
                        <SelectItem value="7">Weekly</SelectItem>
                        <SelectItem value="14">Bi-weekly</SelectItem>
                        <SelectItem value="21">Every 3 weeks</SelectItem>
                        <SelectItem value="30">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="flex space-x-2">
                <Button type="submit" size="sm">
                  Save
                </Button>
                <Button variant="outline" onClick={onCancel} size="sm" type="button">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total People</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{people?.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Candidates</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hiring Managers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {people?.length}
            </div>
          </CardContent>
        </Card>

        {/* Status Card with Toggle (uses RHF, not local state) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Badge variant={displayStatus === "active" ? "default" : "secondary"}>
                {displayStatus}
              </Badge>
              <Button variant="ghost" size="sm" onClick={toggleBucketStatus}>
                {displayStatus === "active" ? "Pause" : "Activate"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* People Table */}
      <Card>
        <CardHeader>
          <CardTitle>People in {displayName}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Last Touch Point</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {people?.map((person) => (
                <TableRow
                  key={person.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handlePersonClick(person.id)}
                >
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-8 w-8">
                        {/*<AvatarImage src={person.avatar || "/placeholder.svg"} alt={person.name} />*/}
                        <AvatarFallback>
                          {`${person.first_name??''} ${person.last_name??''}`
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{`${person.first_name??''} ${person.last_name??''}`}</div>
                        <div className="text-sm text-muted-foreground flex items-center space-x-4">
                          <span className="flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {person.email}
                          </span>
                          <span className="flex items-center">
                            <Building className="h-3 w-3 mr-1" />
                            {person.company_name}
                          </span>
                          {/*
                          <span className="flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            {person.}
                          </span>
                          */}

                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={person.contact_type === "candidate" ? "default" : "secondary"}>
                      {person.contact_type === "candidate" ? "Candidate" : "Hiring Manager"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {person && (person as unknown as { last_touchpoint_at?: string }).last_touchpoint_at
                      ? new Date(
                          (person as unknown as { last_touchpoint_at: string }).last_touchpoint_at
                        ).toLocaleDateString()
                      : "No touchpoint sent"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePersonClick(person.id)
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PersonProfileSheet
        personId={selectedPersonId}
        isOpen={!!selectedPersonId}
        onClose={() => setSelectedPersonId(null)}
      />
    </div>
  )
}
